<script>
import { format, subMonths } from 'date-fns'
import { sync } from 'vuex-pathify'

import Constants from '@/constants'

import SelectOperatorForm from '@/components/SelectOperatorForm.vue'

import AlertMixin from '@/mixins/AlertMixin.vue'
import LoadingMixin from '@/mixins/LoadingMixin.vue'

export default {
  mixins: [
    AlertMixin,
    LoadingMixin
  ],
  computed: {
    apiAccreditations: sync('ApiModule/apiAccreditations'),
    apiClient: sync('ApiModule/apiClient'),
    apiClientBookings: sync('ApiModule/apiClientBookings'),
    apiClientCredits: sync('ApiModule/apiClientCredits'),
    apiClients: sync('ApiModule/apiClients'),
    apiClientTokens: sync('ApiModule/apiClientTokens'),
    apiLocations: sync('ApiModule/apiLocations'),
    apiMixinInitialised: sync('ApiModule/apiMixinInitialised'),
    apiMixinInitialising: sync('ApiModule/apiMixinInitialising'),
    apiNotes: sync('ApiModule/apiNotes'),
    apiOperator: sync('ApiModule/apiOperator'),
    apiOperators: sync('ApiModule/apiOperators'),
    apiOperatorsFromApiJson: sync('ApiModule/apiOperatorsFromApiJson'),
    apiOperatorUsers: sync('ApiModule/apiOperatorUsers'),
    apiProfessional: sync('ApiModule/apiProfessional'),
    apiProfessionalBookings: sync('ApiModule/apiProfessionalBookings'),
    apiProfessionals: sync('ApiModule/apiProfessionals'),
    apiProfessionalSlots: sync('ApiModule/apiProfessionalSlots'),
    apiProfessionalTransactionPrices: sync('ApiModule/apiProfessionalTransactionPrices'),
    apiProfessions: sync('ApiModule/apiProfessions'),
    apiServices: sync('ApiModule/apiServices'),
    apiStatusInterval: sync('ApiModule/apiStatusInterval'),
    apiToken: sync('ApiModule/apiToken'),
    apiTokenExpiry: sync('ApiModule/apiTokenExpiry'),
    apiTokenInterval: sync('ApiModule/apiTokenInterval'),
    apiUserId: sync('ApiModule/apiUserId'),
    apiVouchers: sync('ApiModule/apiVouchers'),
    zoomAuthorised: sync('ApiModule/zoomAuthorised'),
    hasInvalidClientProfile () {
      return this.hasInvalidClientStatus
    },
    hasInvalidClientStatus () {
      return this.apiToken && this.apiClient && this.apiClient.status === Constants.STATUS_UNVERIFIED
    },
    hasInvalidProfessionalLocations () {
      return this.apiToken && this.apiProfessional && this.apiProfessional.locations && this.apiProfessional.locations.length === 0
    },
    hasInvalidProfessionalPrices () {
      return this.apiToken && this.apiProfessional && this.apiProfessional.prices && this.apiProfessional.prices.length === 0
    },
    hasInvalidProfessionalProfile () {
      return this.hasInvalidProfessionalPrices || this.hasInvalidProfessionalSlots || this.hasInvalidProfessionalStatus
    },
    hasInvalidProfessionalSlots () {
      return this.apiToken && this.apiProfessionalSlots && this.apiProfessionalSlots.length === 0
    },
    hasInvalidProfessionalStatus () {
      return this.apiToken && this.apiProfessional && this.apiProfessional.status === Constants.STATUS_UNVERIFIED
    },
    isDwffOperator () {
      return this.apiOperator === Constants.OPERATOR_DWFF
    },
    isGoDoOperator () {
      return this.apiOperator === Constants.OPERATOR_GODO
    },
    isOperatorWithGyms () {
      return this.isDwffOperator || this.isPureGymOperator
    },
    isOperatorWithOnlineGyms () {
      return this.isPureGymOperator
    },
    isOperatorWithLocations () {
      return this.isDwffOperator || this.isGoDoOperator || this.isPureGymOperator || this.isStarOperator
    },
    isPureGymOperator () {
      return this.apiOperator === Constants.OPERATOR_PUREGYM
    },
    isStarOperator () {
      return this.apiOperator === Constants.OPERATOR_STAR
    },
    operatorName () {
      return this.apiOperators.find((operator) => operator.id === this.apiOperator).name
    }
  },
  async mounted () {
    await this.initialise()
  },
  created () {
    this.apiCheckStatusIntervalStart()
    if (this.apiTokenExpiry) {
      if (new Date().valueOf() > this.apiTokenExpiry) {
        this.apiLogOutImpl()
      }
    }
    if (this.apiTokenExpiry && !this.apiTokenInterval) {
      this.apiCheckTokenIntervalStart()
    }
  },
  methods: {
    addOperatorHeader (headers, operatorId = this.apiOperator || Constants.OPERATOR) {
      headers['Operator-Id'] = operatorId
      headers.Origin = window.location.origin
      return headers
    },
    apiCheckStatusIntervalStart () {
      if (this.apiStatusInterval === null) {
        if ((this.apiClient && this.apiClient.status === Constants.STATUS_UNVERIFIED) || (this.apiProfessional && this.apiProfessional.status === Constants.STATUS_UNVERIFIED)) {
          this.apiStatusInterval = setInterval(this.apiCheckStatusInterval, 5000)
        }
      }
    },
    apiCheckStatusIntervalStop () {
      if (this.apiStatusInterval !== null) {
        clearInterval(this.apiStatusInterval)
        this.apiStatusInterval = null
      }
    },
    apiCheckTokenIntervalStart () {
      this.apiTokenInterval = setInterval(this.apiCheckTokenInterval, 1000)
    },
    apiCheckTokenIntervalStop () {
      clearInterval(this.apiTokenInterval)
      this.apiTokenInterval = null
    },
    apiLogOutImpl () {
      this.apiClient = null
      this.apiOperator = null
      this.apiOperatorUsers = null
      this.apiOperators = this.apiOperatorsFromApiJson
      this.apiProfessional = null
      this.apiProfessionalSlots = []
      this.apiToken = null
      this.apiTokenExpiry = null
      this.apiUserId = null
      this.zoomAuthorised = null
    },
    apiThrowJsonErrors (jsonErrors) {
      const string = Object.values(jsonErrors).reduce((accumulator, currentValue) => {
        return `${accumulator} ${currentValue}`
      }, '')
      this.apiThrowString(string)
    },
    apiThrowString (string) {
      this.alertDisplayFailure(string)
      throw new Error(string)
    },
    async apiAuthenticated (json, phone) {
      try {
        this.apiCheckStatusIntervalStop()
        this.apiCheckTokenIntervalStop()
        this.apiUserId = this.extractUserId(this.apiOperator)
        if (json?.meta?.auth?.access_token && json?.meta?.auth?.expires_in) { // eslint-disable-line camelcase
          this.apiToken = json.meta.auth.access_token
          this.apiTokenExpiry = this.tokenExpiry(json.meta.auth.expires_in)
        }
        this.apiCheckStatusIntervalStart()
        this.apiCheckTokenIntervalStart()

        if (Constants.USER_TYPE === Constants.USER_TYPE_CLIENT) {
          await this.apiGetClient(this.apiUserId)
          if (phone) {
            await this.apiUpdateClient(this.apiClient.id, '', this.apiClient.name, phone)
          }
        } else {
          await this.apiGetProfessional(this.apiUserId)
        }
        await this.reinitialise()
        this.routerReplace('/')
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCheckStatusInterval () {
      if (this.apiClient) {
        const apiClient = await this.apiGetClient(this.apiClient.id, false, false)
        if (apiClient && apiClient.status === Constants.STATUS_VERIFIED) {
          this.apiCheckStatusIntervalStop()
          await this.apiGetClient(this.apiClient.id, false, true)
        }
      } else if (this.apiProfessional) {
        const apiProfessional = await this.apiGetProfessional(this.apiProfessional.id, false, false)
        if (apiProfessional && apiProfessional.status === Constants.STATUS_VERIFIED) {
          this.apiCheckStatusIntervalStop()
          this.apiProfessional.status = Constants.STATUS_VERIFIED
        }
      }
    },
    async apiCheckTokenInterval () {
      if (new Date().valueOf() > this.apiTokenExpiry) {
        await this.apiLogOut()
      }
    },
    async apiCreateBooking (bookingEnd, bookingStart, clientId, locationId, professionalId, note = null) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            ...{
              booking_end: bookingEnd,
              booking_start: bookingStart,
              client_id: clientId
            },
            ...(locationId) && {
              location_id: locationId
            }
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional/${professionalId}/booking`, options)
        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        const bookingId = json.data.id
        if (note) {
          await this.apiCreateBookingNote(professionalId, bookingId, note)
        }
        await this.apiGetBookings()
        this.$root.$emit('bookingCreated')
        if (clientId === Constants.EXTERNAL_CLIENT_ID) {
          this.alertDisplaySuccess('Your external booking has been placed.')
        } else {
          const professionalName = this.apiProfessionals.find((professional) => professional.id === professionalId).name
          this.alertDisplaySuccess(`Your request has been sent. ${professionalName} will confirm the booking soon.`)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateBookingNote (professionalId, bookingId, note) {
      try {
        await this.loadingDisplay()
        const optionsNote = {
          body: JSON.stringify({
            description: note,
            type: 'General'
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        optionsNote.headers = this.addOperatorHeader(optionsNote.headers)
        const responseNote = await fetch(`${Constants.URL}/note`, optionsNote)
        if (responseNote.status !== 201) {
          await this.apiThrowResponse(responseNote)
        }
        const jsonNote = await responseNote.json()
        const noteId = jsonNote.data.id
        const optionsBookingNote = {
          body: JSON.stringify({
            note_id: noteId
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        optionsBookingNote.headers = this.addOperatorHeader(optionsBookingNote.headers)
        const responseBookingNote = await fetch(`${Constants.URL}/professional/${professionalId}/booking/${bookingId}/note`, optionsBookingNote)
        if (responseBookingNote.status !== 200) {
          await this.apiThrowResponse(responseBookingNote)
        }
        await this.apiGetNotes()
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateClientLocation (clientId, locationId, locationName, locationRange) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            location_id: locationId,
            name: locationName,
            range: locationRange
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}/location`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiClient = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateClientToken (clientId, token) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            token: token
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}/token`, options)
        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        }
        await this.apiGetClientTokens(clientId)
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateClientVoucher (clientId, name) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            name: name
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}/voucher`, options)
        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateLocation (location, refresh = false) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          body: JSON.stringify(location),
          headers: headers,
          method: 'POST'
        }

        const response = await fetch(`${Constants.URL}/location`, options)

        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        if (refresh) {
          await this.apiGetLocations()
        }
        return json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateProfessionalAccreditation (professionalId, accreditationId) {
      try {
        await this.loadingDisplay()
        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })
        const options = {
          body: JSON.stringify({ accreditation_id: accreditationId }),
          headers: headers,
          method: 'POST'
        }
        const accreditationType = this.apiAccreditations.find((accreditation) => accreditation.id === accreditationId).type
        const response = await fetch(`${Constants.URL}/professional/${professionalId}/${accreditationType}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateProfessionalLocation (professionalId, professionalLocation) {
      if (!professionalLocation.location.id) {
        const locationJson = await this.apiCreateLocation(professionalLocation.location)
        professionalLocation.location = locationJson
      }

      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          body: JSON.stringify({ location_id: professionalLocation.location.id, name: professionalLocation.name, range: professionalLocation.range }),
          headers: headers,
          method: 'POST'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/location`, options)

        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        this.apiProfessional = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateProfessionalProfession (professionalId, professionId) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          body: JSON.stringify({ profession_id: professionId }),
          headers: headers,
          method: 'POST'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/profession`, options)

        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        this.apiProfessional = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateProfessionalService (professionalId, serviceId) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          body: JSON.stringify({ service_id: serviceId }),
          headers: headers,
          method: 'POST'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/service`, options)

        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        this.apiProfessional = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateProfessionalSlot (endTime, startTime) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            slots: [
              {
                end_time: endTime,
                start_time: startTime
              }
            ]
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional/${this.apiProfessional.id}/availability`, options)
        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiCreateProfessionalTransaction (amountInPennies, clientId, cvcRequired, numberOfCredits, professionalId, token, voucherName) {
      try {
        await this.loadingDisplay()
        if (cvcRequired) {
          const optionsCvcRequired = {
            body: JSON.stringify({
              clientKey: Constants.WORLDPAY,
              cvc: cvcRequired
            }),
            headers: {
              'Content-Type': 'application/json'
            },
            method: 'PUT'
          }
          const responseCvcRequired = await fetch(`https://api.worldpay.com/v1/tokens/${token}`, optionsCvcRequired)
          if (responseCvcRequired.status !== 200) {
            await this.apiThrowString('Unauthorized - invalid cvc.')
          }
        }
        const options = {
          body: JSON.stringify({
            amount: amountInPennies,
            currency: 'GBP',
            number_of_credits: numberOfCredits,
            token: token,
            ...voucherName && { voucher_name: voucherName }
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional/${professionalId}/transaction`, options)
        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        }
        await this.apiGetClientCredits(clientId)
        await this.apiGetClientTokens(clientId)
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiDeleteClientTokens (clientId) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'DELETE'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}/token`, options)
        if (response.status !== 204) {
          await this.apiThrowResponse(response)
        }
        await this.apiGetClientTokens(clientId)
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiDeleteProfessionalAccreditation (professionalId, accreditationId) {
      try {
        await this.loadingDisplay()
        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })
        const options = {
          headers: headers,
          method: 'DELETE'
        }
        const response = await fetch(`${Constants.URL}/professional/${professionalId}/accreditation/${accreditationId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiDeleteProfessionalLocation (professionalId, locationId) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          headers: headers,
          method: 'DELETE'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/location/${locationId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        this.apiProfessional = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiDeleteProfessionalProfession (professionalId, professionId) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          headers: headers,
          method: 'DELETE'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/profession/${professionId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        this.apiProfessional = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiDeleteProfessionalService (professionalId, serviceId) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          headers: headers,
          method: 'DELETE'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/service/${serviceId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        this.apiProfessional = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiDeleteProfessionalSlot (slotId) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'DELETE'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/availability/${slotId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetAccreditations () {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: Constants.AUTHORIZATION,
          'Content-Type': 'application/json'
        })

        const options = {
          headers: headers,
          method: 'GET'
        }

        const response = await fetch(`${Constants.URL}/accreditation`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiAccreditations = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetBookings () {
      this.isClient() ? await this.apiGetClientBookings() : await this.apiGetProfessionalBookings()
    },
    async apiGetClient (clientId, displayLoading = true, assignToStore = true) {
      try {
        if (displayLoading) {
          await this.loadingDisplay()
        }
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        if (assignToStore) {
          this.apiClient = json.data
        }
        return json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetClientBookings (clientId = this.apiClient.id) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const startDate = format(subMonths(new Date(), 1), 'YYYY-MM-DD')
        const response = await fetch(`${Constants.URL}/client/${clientId}/calendar?startDate=${startDate}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiClientBookings = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetClientCredits (clientId) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}/credit`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiClientCredits = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetClients () {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiClients = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetClientTokens (clientId) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}/token`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiClientTokens = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetLocations (coordinates = null) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        let queryParams = ''
        if (coordinates) {
          const { lat, lng } = coordinates
          queryParams = `?lat=${lat}&lng=${lng}`
        }
        const response = await fetch(`${Constants.URL}/location${queryParams}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiLocations = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetNotes () {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/note`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiNotes = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetOperators () {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        const response = await fetch(`${Constants.URLV1}/operator`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiOperatorsFromApiJson = this.extractOperatorsFromApiJson(json)
        this.apiOperators = this.apiOperatorsFromApiJson
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessional (professionalId, displayLoading = true, assignToStore = true) {
      try {
        if (displayLoading) {
          await this.loadingDisplay()
        }
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional/${professionalId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        if (assignToStore) {
          this.apiProfessional = json.data
          this.zoomAuthorised = this.apiProfessional.zoom_authorised
        }
        return json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessionalBookings (professionalId = this.apiProfessional.id) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const startDate = format(subMonths(new Date(), 1), 'YYYY-MM-DD')
        const response = await fetch(`${Constants.URL}/professional/${professionalId}/calendar?startDate=${startDate}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiProfessionalBookings = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessionalLocation (professionalId, locationId) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          headers: headers,
          method: 'GET'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/location/${locationId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        return json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessionals () {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiProfessionals = json.data.sort((a, b) => a.name > b.name ? 1 : a.name < b.name ? -1 : 0)
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessionalsByLocation (originLat, originLng, originNam, originRad) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional?originLat=${originLat}&originLng=${originLng}&originNam=${originNam}&originRad=${originRad}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        return json.data.sort((a, b) => a.locations[0].distanceFromOrigin > b.locations[0].distanceFromOrigin ? 1 : a.locations[0].distanceFromOrigin < b.locations[0].distanceFromOrigin ? -1 : 0)
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessionalSlots (professionalId = this.apiProfessional.id) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional/${professionalId}/availability`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiProfessionalSlots = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessionalTransactionPrices (professionalId, clientId) {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response1 = await fetch(`${Constants.URL}/professional/${professionalId}/transaction/price/1/${clientId}`, options)
        if (response1.status !== 200) {
          await this.apiThrowResponse(response1)
        }
        const json1 = await response1.json()
        const response5 = await fetch(`${Constants.URL}/professional/${professionalId}/transaction/price/5/${clientId}`, options)
        if (response5.status !== 200) {
          await this.apiThrowResponse(response5)
        }
        const json5 = await response5.json()
        const response10 = await fetch(`${Constants.URL}/professional/${professionalId}/transaction/price/10/${clientId}`, options)
        if (response10.status !== 200) {
          await this.apiThrowResponse(response10)
        }
        const json10 = await response10.json()
        this.$store.set('ApiModule/apiProfessionalTransactionPrices', [Object.assign(json1.data, { credits: 1 }), Object.assign(json5.data, { credits: 5 }), Object.assign(json10.data, { credits: 10 })])
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetProfessions () {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: Constants.AUTHORIZATION,
          'Content-Type': 'application/json'
        })

        const options = {
          headers: headers,
          method: 'GET'
        }

        const response = await fetch(`${Constants.URL}/profession`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiProfessions = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetServices () {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: Constants.AUTHORIZATION,
          'Content-Type': 'application/json'
        })

        const options = {
          headers: headers,
          method: 'GET'
        }

        const response = await fetch(`${Constants.URL}/service`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiServices = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiGetVouchers () {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'GET'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/voucher`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiVouchers = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiLogIn (email, password) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            email,
            password
          }),
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/auth/login`, options)
        if (response.status !== 200) {
          if (response.status === 401) {
            await this.apiThrowString('Unauthorized - invalid credentials.')
          } else {
            await this.apiThrowResponse(response)
          }
        }
        this.apiLoginResponseHandler(response)
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiLoginResponseHandler (response) {
      const json = await response.json()
      this.apiOperators = this.extractOperatorsFromJson(json)
      this.apiOperatorUsers = this.extractOperatorUsersFromJson(json)
      if (this.apiOperators.length === 0) {
        await this.apiThrowString('Unauthorized - invalid credentials.')
      } else {
        const sharedOperators = this.extractSharedOperators()
        if (sharedOperators.length === 0) {
          await this.apiThrowString('Unauthorized - invalid credentials.')
        } else if (sharedOperators.length === 1) {
          this.apiOperator = sharedOperators[0].id
          await this.apiAuthenticated(json)
        } else {
          this.$buefy.modal.open({
            canCancel: false,
            component: SelectOperatorForm,
            hasModalCard: true,
            parent: this,
            props: {
              canClose: false,
              json,
              impl: this.selectOperatorFormImpl,
              noun: 'Operator',
              operators: sharedOperators,
              verbBase: 'Select',
              verbGerund: 'Selecting'
            }
          })
        }
      }
      // TODO upload fcmToken if present
    },
    async apiLogInWithToken (accessToken, expiresIn, operatorId, userId) {
      if (!this.apiToken && !!accessToken && !!expiresIn && !!operatorId && !!userId) {
        try {
          await this.loadingDisplay()
          const options = {
            headers: {
              Accept: 'application/json',
              Authorization: `${Constants.BEARER} ${accessToken}`,
              'Content-Type': 'application/json',
              'Operator-Id': `${operatorId}`
            },
            method: 'GET'
          }
          const response = await fetch(`${Constants.URLV1}/user/${userId}`, options)
          if (response.status === 200) {
            this.apiToken = accessToken
            this.apiTokenExpiry = this.tokenExpiry(parseInt(expiresIn))
            this.apiLoginResponseHandler(response)
          }
        } finally {
          await this.loadingDismiss()
        }
      }
    },
    async apiLogOut () {
      try {
        await this.loadingDisplay()
        this.apiLogOutImpl()
        this.apiCheckStatusIntervalStop()
        this.apiCheckTokenIntervalStop()
        this.routerPush('/')
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiResendVerificationEmail () {
      try {
        await this.loadingDisplay()
        const options = {
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          }
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/auth/resendVerificationEmail`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        this.alertDisplaySuccess('A verification email has been sent.')
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiResetPassword (email) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            email
          }),
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/auth/password/forgot`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        this.alertDisplaySuccess(`An email with further instructions has been sent to ${email}.`)
        this.routerPush('/')
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiSignUp (email, name, operator, password, phone = null) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            email,
            name,
            password
          }),
          headers: {
            Accept: 'application/json',
            Authorization: Constants.AUTHORIZATION,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers, operator)
        const response = await fetch(`${Constants.URLV1}/${Constants.USER_TYPE}`, options)
        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        this.apiOperators = this.extractOperatorsFromJson(json)
        this.apiOperatorUsers = this.extractOperatorUsersFromJson(json)
        this.apiOperator = operator
        await this.apiAuthenticated(json, phone)
        // TODO upload fcmToken if present
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiThrowResponse (response) {
      const json = await response.json()
      const statusText = response.statusText && response.statusText !== 'No Reason Phrase' && response.statusText !== 'Unprocessable Entity' ? response.statusText : JSON.stringify(json)
      if (response.status === 500 || statusText.includes('SQLSTATE[HY000] [')) {
        this.apiThrowString('There is a problem on our side. Please try again later.')
      } else if (response.status === 422 && response.url.includes('getTokensWithRefreshToken') && statusText.includes('Invalid Token!')) {
        this.zoomAuthorised = null
        this.apiThrowString('You have been logged out of Zoom.')
      } else if (response.status === 422 && statusText.includes('The supplied operator-id header is invalid.')) {
        this.apiThrowString('Unauthorized - invalid credentials.')
      } else if (response.status === 422 && statusText.includes('The booking must not overlap an existing accepted booking.')) {
        this.$root.$emit('initialiseOverlappingBooking')
        this.apiThrowString('Your trainer\'s calendar has just been updated. Please, choose another date and/or time.')
      } else if (response.status === 422 && statusText.includes('The name has already been taken.') && response.url.match(/\/client\/\d+\/voucher/)) {
        this.apiThrowString('The voucher code has already been entered.')
      } else if (response.status === 422 && statusText.includes('Changes to the slot will affect')) {
        this.apiThrowString('The requested working hours clash with existing bookings.')
      } else if (response.status === 409 && statusText.includes('The proposed slot clashes with existing slot')) {
        this.apiThrowString('The requested working hours clash with existing working hours.')
      } else {
        this.apiThrowJsonErrors(json.errors)
      }
    },
    async apiUpdateBooking (professionalId, bookingId, bookingStatus) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            booking_status: bookingStatus
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'PUT'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional/${professionalId}/booking/${bookingId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        await this.apiGetBookings()
        this.$root.$emit('bookingUpdated')
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiUpdateClient (clientId, goal, name, phone) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            name,
            phone,
            training_target: goal
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'PUT'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/client/${clientId}`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        await this.apiGetClient(this.apiClient.id)
        this.alertDisplaySuccess('Your profile has been saved.')
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiUpdateProfessional (professionalId, avatar, description, dob, gender, headline, insurance, locationDescription, locationsToDelete, locationsToInsert, membershipsToDelete, membershipsToInsert, name, phone, price1, price5, price10, professionsToDelete, professionsToInsert, qualificationsToDelete, qualificationsToInsert, servicesToDelete, servicesToInsert) {
      try {
        await this.loadingDisplay()
        const options1 = {
          body: JSON.stringify({
            ...(avatar?.startsWith('data:image')) && {
              avatar_image: avatar
            },
            description: encodeURIComponent(description),
            dob: format(dob, 'YYYY-MM-DD'),
            gender,
            insurance,
            location_description: encodeURIComponent(locationDescription),
            name,
            phone,
            tagline: encodeURIComponent(headline)
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'PUT'
        }
        options1.headers = this.addOperatorHeader(options1.headers)
        const response1 = await fetch(`${Constants.URL}/professional/${professionalId}`, options1)
        if (response1.status !== 200) {
          await this.apiThrowResponse(response1)
        }

        const options2 = {
          body: JSON.stringify({
            price1,
            price5,
            price10,
            type: 'General'
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options2.headers = this.addOperatorHeader(options2.headers)
        const response2 = await fetch(`${Constants.URL}/professional/${professionalId}/price`, options2)
        if (response2.status !== 201) {
          await this.apiThrowResponse(response2)
        }

        locationsToDelete.forEach(async (locationToDelete) => this.apiDeleteProfessionalLocation(professionalId, locationToDelete))
        locationsToInsert.forEach(async (locationToInsert) => this.apiCreateProfessionalLocation(professionalId, locationToInsert))

        membershipsToDelete.forEach(async (membershipToDelete) => this.apiDeleteProfessionalAccreditation(professionalId, membershipToDelete.id))
        membershipsToInsert.forEach(async (membershipToInsert) => this.apiCreateProfessionalAccreditation(professionalId, membershipToInsert.id))

        qualificationsToDelete.forEach(async (qualificationToDelete) => this.apiDeleteProfessionalAccreditation(professionalId, qualificationToDelete.id))
        qualificationsToInsert.forEach(async (qualificationToInsert) => this.apiCreateProfessionalAccreditation(professionalId, qualificationToInsert.id))

        professionsToDelete.forEach(async (professionToDelete) => this.apiDeleteProfessionalProfession(professionalId, professionToDelete.id))
        professionsToInsert.forEach(async (professionToInsert) => this.apiCreateProfessionalProfession(professionalId, professionToInsert.id))

        servicesToDelete.forEach(async (serviceToDelete) => this.apiDeleteProfessionalService(professionalId, serviceToDelete.id))
        servicesToInsert.forEach(async (serviceToInsert) => this.apiCreateProfessionalService(professionalId, serviceToInsert.id))

        await this.apiGetProfessional(this.apiProfessional.id)
        this.alertDisplaySuccess('Your profile has been saved.')
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiUpdateProfessionalLocation (professionalId, locationId, professionalLocation) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          body: JSON.stringify(professionalLocation),
          headers: headers,
          method: 'PUT'
        }

        const response = await fetch(`${Constants.URL}/professional/${professionalId}/location/${locationId}`, options)

        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }

        const json = await response.json()
        this.apiProfessional = json.data
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiUpdateProfessionalSlot (endTime, slotId, startTime) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            slots: [
              {
                apply: 'single',
                end_time: endTime,
                id: slotId,
                start_time: startTime
              }
            ]
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'PUT'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URL}/professional/${this.apiProfessional.id}/availability`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async apiUpdateUserServices (userId, services) {
      try {
        await this.loadingDisplay()

        const headers = this.addOperatorHeader({
          Accept: 'application/json',
          Authorization: `${Constants.BEARER} ${this.apiToken}`,
          'Content-Type': 'application/json'
        })

        const options = {
          body: JSON.stringify(services),
          headers: headers,
          method: 'PUT'
        }

        const response = await fetch(`${Constants.URL}/service/user/${userId}`, options)

        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async initialise () {
      if (!this.apiMixinInitialising) {
        this.apiMixinInitialising = true
        if (this.apiOperators.length === 0) {
          await this.apiGetOperators()
        }
        if (this.isClient()) {
          await this.initialiseClient()
        }
        if (this.isProfessional()) {
          await this.initialiseProfessional()
        }
        this.apiMixinInitialised = true
      }
    },
    async initialiseClient () {
      if (this.apiToken) {
        await this.apiGetAccreditations()
        await this.apiGetClientBookings()
        await this.apiGetLocations()
        await this.apiGetProfessionals()
        await this.apiGetServices()
      }
    },
    async initialiseProfessional () {
      if (this.apiToken) {
        await this.apiGetClients()
        await this.apiGetLocations()
        await this.apiGetProfessionalBookings()
        await this.apiGetProfessionalSlots()
        await this.apiGetProfessionals()
      }
    },
    async reinitialise () {
      this.apiMixinInitialising = false
      this.apiMixinInitialised = false
      await this.initialise()
    },
    async selectOperatorFormImpl (json, operator) {
      if (this.apiOperator === null || this.apiOperator !== operator) {
        this.apiOperator = operator
        await this.apiAuthenticated(json)
      }
    },
    extractOperatorsFromApiJson (json) {
      return json.data.map((operator) => ({
        canLogIn: operator.attributes.login === 1,
        canSignUp: operator.attributes.register === 1,
        id: operator.id,
        name: operator.attributes.name
      }))
    },
    extractOperatorsFromJson (json) {
      const userOperators = json.data.relationships[`${Constants.USER_TYPE}s`].map((user) => user.operator_id)
      return this.apiOperators.filter((operator) => userOperators.includes(operator.id))
    },
    extractOperatorUsersFromJson (json) {
      return json.data.relationships[`${Constants.USER_TYPE}s`].map((user) => {
        return {
          id: user.id,
          operator_id: user.operator_id
        }
      })
    },
    extractSharedOperators () {
      if (!!this.apiOperatorUsers && !!this.apiOperators) {
        const apiUserOperatorIds = this.apiOperatorUsers.map((apiOperatorUser) => apiOperatorUser.operator_id)
        return this.apiOperators.filter((operator) => apiUserOperatorIds.includes(operator.id))
      } else {
        return []
      }
    },
    extractUserId (operator) {
      return this.apiOperatorUsers.find((operatorUser) => operatorUser.operator_id === operator).id
    },
    isClient () {
      return Constants.USER_TYPE === Constants.USER_TYPE_CLIENT
    },
    isMultiOperator () {
      return this.extractSharedOperators().length > 1
    },
    isProfessional () {
      return Constants.USER_TYPE === Constants.USER_TYPE_PROFESSIONAL
    },
    isZoomClient () {
      return Constants.ZOOM_REDIRECT_URI !== ''
    },
    routerPush (location) {
      this.$router.push(location).catch(() => {})
    },
    routerReplace (location) {
      this.$router.replace(location).catch(() => {})
    },
    async sleep (ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    tokenExpiry (expiresIn) {
      return new Date().valueOf() + expiresIn * 1000
    },
    async zoomGetAuthorization () {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({}),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/professional/${this.apiProfessional.id}/zoomGetClientId`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        const json = await response.json()
        const clientId = json.data.client_id
        const redirectUri = encodeURIComponent(window.location.href + Constants.ZOOM_REDIRECT_URI)
        const responseType = 'code'
        window.location.replace(`https://zoom.us/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=${responseType}`)
      } finally {
        await this.loadingDismiss()
      }
    },
    async zoomGetDeauthorization () {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({}),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/professional/${this.apiProfessional.id}/zoomRevokeTokensWithAccessToken`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        this.zoomAuthorised = null
      } finally {
        await this.loadingDismiss()
      }
    },
    async zoomGetTokensWithCode () {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            code: this.$route.query.code,
            redirect_uri: encodeURIComponent(window.location.origin + window.location.pathname)
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/professional/${this.apiProfessional.id}/zoomGetTokensWithCode`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
        this.zoomAuthorised = true
        this.routerPush('/zoom')
      } finally {
        await this.loadingDismiss()
      }
    },
    async zoomGetTokensWithRefreshToken () {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({}),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/professional/${this.apiProfessional.id}/zoomGetTokensWithRefreshToken`, options)
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async zoomDeleteUri (uri) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            uri: encodeURIComponent(uri)
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/professional/${this.apiProfessional.id}/zoomDeleteUri`, options)
        if (response.status !== 204) {
          const json = await response.json()
          if (json.errors && json.errors.zoom && json.errors.zoom.includes('Access token is expired.')) {
            await this.zoomGetTokensWithRefreshToken()
            return await this.zoomDeleteUri(uri)
          } else {
            this.apiThrowJsonErrors(json.errors)
          }
        }
      } finally {
        await this.loadingDismiss()
      }
    },
    async zoomGetMe () {
      return await this.zoomGetUri('https://api.zoom.us/v2/users/me')
    },
    async zoomGetMeetings () {
      const meetings = { data: { meetings: [] } }
      const pageSize = 300 // Zoom page size: default 30, maximum 300
      const type = 'upcoming' // Zoom meeting types: scheduled|live|upcoming
      let nextPageToken = null
      do {
        await this.sleep(100) // Zoom rate limit: 10 requests/second
        const uri = `https://api.zoom.us/v2/users/me/meetings?page_size=${pageSize}&type=${type}` + (nextPageToken ? `&next_page_token=${nextPageToken}` : '')
        const pagedMeetings = await this.zoomGetUri(uri)
        if (pagedMeetings.data.meetings.length > 0) {
          meetings.data.meetings.push(...pagedMeetings.data.meetings)
        }
        nextPageToken = pagedMeetings.data.next_page_token
      } while (nextPageToken)

      return meetings
    },
    async zoomGetUri (uri) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            uri: encodeURIComponent(uri)
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/professional/${this.apiProfessional.id}/zoomGetUri`, options)
        const json = await response.json()
        if (response.status !== 200) {
          await this.apiThrowResponse(response)
        } else if (response.status === 200 && json.data.message === 'Access token is expired.') {
          await this.zoomGetTokensWithRefreshToken()
          return await this.zoomGetUri(uri)
        }
        return json
      } finally {
        await this.loadingDismiss()
      }
    },
    async zoomPostUri (uri, data) {
      try {
        await this.loadingDisplay()
        const options = {
          body: JSON.stringify({
            payload: JSON.stringify(data),
            uri: encodeURIComponent(uri)
          }),
          headers: {
            Accept: 'application/json',
            Authorization: `${Constants.BEARER} ${this.apiToken}`,
            'Content-Type': 'application/json'
          },
          method: 'POST'
        }
        options.headers = this.addOperatorHeader(options.headers)
        const response = await fetch(`${Constants.URLV1}/professional/${this.apiProfessional.id}/zoomPostUri`, options)
        const json = await response.json()
        if (response.status !== 201) {
          await this.apiThrowResponse(response)
        } else if (response.status === 201 && json.data.message === 'Access token is expired.') {
          await this.zoomGetTokensWithRefreshToken()
          return await this.zoomPostUri(uri, data)
        }
        return json
      } finally {
        await this.loadingDismiss()
      }
    }
  }
}
</script>
